import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PdfRegion as DsPdfRegion, configurePdfWorker } from 'admination-react'
import { downloadFile } from '@/core/data/storage'
import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

configurePdfWorker(workerUrl)

export const ZOOMED_OUT_PDF_SCALE = 0.85

type PdfRegionSource =
  | {
      path: null
      waiting?: false
      loadingLabel?: undefined
    }
  | {
      path: string | null

      waiting?: boolean
      loadingLabel: string
    }

export function PdfRegion({
  path,
  localBlob = null,
  waiting = false,
  loadingLabel,
  placeholder,
  notice,
  download = downloadFile,
  scaleFactor = 1,
  inline = false,
  reading = false,
  testId,
}: PdfRegionSource & {

  localBlob?: Blob | readonly Blob[] | null
  notice?: ReactNode
  placeholder: string

  download?: (path: string) => Promise<Blob | null>

  scaleFactor?: number

  inline?: boolean

  reading?: boolean

  testId?: string
}) {
  const { t, i18n } = useTranslation('portal')
  const [blob, setBlob] = useState<Blob | readonly Blob[] | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    if (localBlob) {
      setBlob(localBlob)
      setLoading(false)
      return
    }

    if (waiting) {
      setBlob(null)
      setLoading(true)
      return
    }
    if (!path) {
      setBlob(null)
      setLoading(false)
      return
    }
    setLoading(true)
    const resolvedPath = path
    let attempts = 0
    const MAX_ATTEMPTS = 30
    async function tryDownload() {
      if (cancelled) return
      const data = await download(resolvedPath)
      if (cancelled) return
      if (data) {
        setBlob(data)
        setLoading(false)
      } else if (attempts < MAX_ATTEMPTS) {
        attempts++
        setTimeout(() => void tryDownload(), 500)
      } else {
        setLoading(false)
      }
    }
    void tryDownload()
    return () => {
      cancelled = true
    }
  }, [path, localBlob, waiting, download])

  return (
    <DsPdfRegion
      blob={blob}
      loading={loading}
      loadingLabel={loadingLabel ?? ''}
      scaleFactor={scaleFactor}
      placeholder={placeholder}
      imageAlt="Dokumentvorschau"

      zoomLabels={{
        zoomOut: t('document.zoomOut'),
        zoomIn: t('document.zoomIn'),
        reset: t('document.zoomReset'),
        level: (zoom) => new Intl.NumberFormat(i18n.language, { style: 'percent' }).format(zoom),
      }}
      notice={notice}
      inline={inline}
      reading={reading}
      data-testid={testId}
    />
  )
}
